/**
 * Sound Player Service for push notifications
 * Handles audio playback with fallbacks and user gesture requirements
 */
class SoundPlayer {
  constructor() {
    this.audioContext = null;
    this.isInitialized = false;
    this.soundFiles = {
      notification: `/sounds/notification.mp3?v=${Date.now()}`
    };
    this.fallbackSounds = new Map();
    
    // Initialize after user gesture
    this.initPromise = null;
  }

  /**
   * Initialize audio context - requires user gesture
   */
  async initialize() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  async _doInitialize() {
    try {
      // Create audio context
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Preload sound files
      await this.preloadSounds();
      
      this.isInitialized = true;
      console.log('🔊 SoundPlayer initialized successfully');
      
    } catch (error) {
      console.warn('Failed to initialize SoundPlayer:', error);
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Preload sound files into Audio objects
   */
  async preloadSounds() {
    const promises = Object.entries(this.soundFiles).map(async ([name, url]) => {
      try {
        const audio = new Audio(url);
        audio.preload = 'auto';
        audio.volume = 0.5;
        
        // Wait for audio to be loadable
        await new Promise((resolve, reject) => {
          audio.addEventListener('canplay', resolve, { once: true });
          audio.addEventListener('error', reject, { once: true });
          audio.load();
        });
        
        this.fallbackSounds.set(name, audio);
        console.log(`🔊 Preloaded sound: ${name}`);
        
      } catch (error) {
        console.warn(`Failed to preload sound ${name}:`, error);
      }
    });

    await Promise.allSettled(promises);
  }

  /**
   * Play notification sound with Web Audio API
   */
  async playNotificationSound() {
    try {
      // Try to initialize if not done
      if (!this.isInitialized) {
        await this.initialize();
      }

      // Try Web Audio API first
      if (this.audioContext && this.audioContext.state === 'running') {
        await this.playWebAudioNotification();
        return;
      }

      // Fallback to HTML5 Audio
      await this.playHTMLAudioNotification();
      
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
      // Try emergency fallback
      this.playEmergencyFallback();
    }
  }

  /**
   * Play sound using Web Audio API (better quality/control)
   */
  async playWebAudioNotification() {
    if (!this.audioContext) throw new Error('AudioContext not available');

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    // Create pleasant notification sound
    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime + 0.2);
    
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.1, this.audioContext.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.4);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.4);
    
    console.log('🔊 Web Audio notification played');
  }

  /**
   * Play sound using HTML5 Audio element
   */
  async playHTMLAudioNotification() {
    const audio = this.fallbackSounds.get('notification');
    
    if (audio) {
      // Clone to allow multiple simultaneous plays
      const audioClone = audio.cloneNode();
      audioClone.volume = 0.5;
      
      try {
        await audioClone.play();
        console.log('🔊 HTML5 Audio notification played');
      } catch (playError) {
        console.warn('HTML5 Audio play failed:', playError);
        throw playError;
      }
    } else {
      throw new Error('No preloaded audio available');
    }
  }

  /**
   * Emergency fallback using data URI
   */
  playEmergencyFallback() {
    try {
      // Short beep sound as data URI
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwss5OzwygdePh3y9/nZCzM/dT/8zw==');
      audio.volume = 0.3;
      audio.play().catch(() => {
        console.warn('Emergency fallback sound also failed');
      });
      
      console.log('🔊 Emergency fallback sound played');
    } catch (error) {
      console.warn('Emergency fallback failed:', error);
    }
  }

  /**
   * Create user gesture button for initialization
   */
  createSoundPrimer() {
    const button = document.createElement('button');
    button.textContent = '🔊 Enable Sounds';
    button.className = 'btn btn-sm btn-outline-primary';
    button.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      padding: 8px 12px;
      border: 1px solid #007bff;
      background: white;
      color: #007bff;
      border-radius: 4px;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    `;

    button.addEventListener('click', async () => {
      try {
        await this.initialize();
        await this.playNotificationSound(); // Test play
        button.remove();
        
        // Store that user has enabled sounds
        localStorage.setItem('soundsEnabled', 'true');
        
      } catch (error) {
        console.error('Failed to enable sounds:', error);
        button.textContent = '❌ Sound Failed';
      }
    });

    return button;
  }

  /**
   * Check if sounds are enabled and working
   */
  isSoundEnabled() {
    // If not set yet, default to enabled
    if (localStorage.getItem('soundsEnabled') === null) {
      localStorage.setItem('soundsEnabled', 'true');
    }
    return localStorage.getItem('soundsEnabled') === 'true';
  }

  /**
   * Force reload sound files (useful when sounds are updated)
   */
  async reloadSounds() {
    console.log('🔄 Reloading sound files...');
    
    // Update URLs with new timestamp
    this.soundFiles.notification = `/sounds/notification.mp3?v=${Date.now()}`;
    
    // Clear cached sounds
    this.fallbackSounds.clear();
    
    // Reload if initialized
    if (this.isInitialized) {
      await this.preloadSounds();
      console.log('✅ Sound files reloaded');
    }
  }

  /**
   * Reset audio context (useful for debugging)
   */
  async reset() {
    if (this.audioContext) {
      await this.audioContext.close();
    }
    
    this.audioContext = null;
    this.isInitialized = false;
    this.initPromise = null;
    this.fallbackSounds.clear();
    
    localStorage.removeItem('soundsEnabled');
    console.log('🔊 SoundPlayer reset');
  }

  /**
   * Get current status for debugging
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      audioContextState: this.audioContext?.state,
      soundsEnabled: this.isSoundEnabled(),
      preloadedSounds: Array.from(this.fallbackSounds.keys())
    };
  }
}

// Export singleton instance
const soundPlayer = new SoundPlayer();
export default soundPlayer;